import { useEffect, useState } from "react";
import { Box, Button, Group, Modal, SimpleGrid, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { renameProviderDocument } from "../../services/providerDocuments";
import useStyles from "./style";

/**
 * Rename a provider document. Mirrors the staff/client rename modal — the
 * extension is left off the input and re-applied by the backend, so a document
 * can never be renamed into a type it isn't.
 */
const ProviderRenameFileModal = ({ opened, onClose, providerId, data, onSaved }) => {
  const { classes } = useStyles();
  const [saving, setSaving] = useState(false);

  const form = useForm({
    initialValues: { name: "" },
    validate: {
      name: (value) => (value?.trim() ? null : "Please specify a file name"),
    },
  });

  // The modal stays mounted between openings, so seed the field each time it
  // opens with the name (minus extension) of whichever file it was opened for.
  useEffect(() => {
    if (opened) form.setValues({ name: data?.name?.replace(/\.[^.]+$/, "") || "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, data?._id, data?.id]);

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const onSave = async (values) => {
    setSaving(true);
    const renamed = await renameProviderDocument(providerId, data?._id || data?.id, values.name.trim());
    setSaving(false);

    if (renamed) {
      onSaved?.();
      handleClose();
    }
  };

  return (
    <Modal
      className={classes.folderModal}
      opened={opened}
      size="lg"
      onClose={handleClose}
      shadow="md"
      overlayProps={{ blur: 3 }}
      radius="lg"
      zIndex={1000}
      centered
      title={
        <Box>
          <Text sx={{ fontFamily: "Inter", fontSize: 22, fontWeight: 600 }}>Rename File</Text>
          <Text sx={{ fontFamily: "Inter", fontSize: 13, color: "#667085" }} title={data?.name}>
            {data?.name}
          </Text>
        </Box>
      }
    >
      <form onSubmit={form.onSubmit(onSave)}>
        <SimpleGrid className={classes.folderModalBody}>
          <TextInput
            label="Name"
            size="md"
            placeholder="File Name"
            data-autofocus
            sx={{ fontFamily: "Inter" }}
            {...form.getInputProps("name")}
          />
        </SimpleGrid>

        <Group mt={20} grow>
          <Button
            color="gray"
            variant="outline"
            c="#000"
            fullWidth
            sx={{ fontFamily: "Inter" }}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            c="#000"
            color="yellow"
            sx={{ fontFamily: "Inter" }}
            type="submit"
            loading={saving}
          >
            Update
          </Button>
        </Group>
      </form>
    </Modal>
  );
};

export default ProviderRenameFileModal;
